// InstallationGuide.js — Installation (refonte T6).
// Migrée sur le design system (dark mode). Suppression des classes Tailwind dynamiques (text-${color})
// non générées par le JIT (remplacées par des styles inline fiables). Chargement via require.context
// (mécanisme conservé). Schéma composant inchangé (metadata + platforms{os}{steps}). Rétro-compatible.
import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, FileCode, Package, Globe } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, Badge, CodeBlock, EmptyState, Icon } from '../components/ui';

const componentContext = require.context('../data/components', false, /\.json$/);

const OPERATING_SYSTEMS = [
  { id: 'ubuntu', name: 'Ubuntu/Debian', icon: '🐧' },
  { id: 'rhel', name: 'RHEL/CentOS', icon: '🎩' },
  { id: 'windows', name: 'Windows Server', icon: '🪟' },
];

const CATEGORY = {
  Backend: { color: '#10B981', icon: 'Server' },
  Frontend: { color: '#3B82F6', icon: 'Globe' },
  Database: { color: '#6366F1', icon: 'Database' },
  Agent: { color: '#EF4444', icon: 'Cpu' },
  Monitoring: { color: '#F59E0B', icon: 'Activity' },
  Orchestration: { color: '#8B5CF6', icon: 'Layers' },
  Other: { color: '#6B7280', icon: 'Package' },
};

const detectCategory = (meta = {}) => {
  const s = `${meta.title || ''} ${meta.component || ''} ${meta.description || ''}`.toLowerCase();
  if (/(backend|spring|java)/.test(s)) return 'Backend';
  if (/(frontend|nginx|react)/.test(s)) return 'Frontend';
  if (/(collecteur|genero)/.test(s)) return 'Agent';
  if (/(database|postgres|oracle|informix|sql)/.test(s)) return 'Database';
  if (/(orchestr|docker|kubernetes|compose|systemd)/.test(s)) return 'Orchestration';
  if (/(monitoring|prometheus|grafana)/.test(s)) return 'Monitoring';
  if (/agent/.test(s)) return 'Agent';
  return 'Other';
};

const CommandBlock = ({ command, description, note, language = 'bash' }) => (
  <div className="my-3">
    {description && <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">{description}</div>}
    <CodeBlock code={command} language={language} />
    {note && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{note}</div>}
  </div>
);

const InstallationSteps = ({ data, os }) => {
  const platform = data.platforms?.[os];
  if (!platform) return null;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Installation — {platform.name}</h3>
        <Badge color="#3B82F6">{platform.name}</Badge>
      </div>
      {(platform.steps || []).map((step, i) => (
        <Card key={step.id || i}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300 w-8 h-8 rounded-full flex items-center justify-center font-semibold">{i + 1}</span>
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step.description && <p className="text-gray-600 dark:text-gray-300">{step.description}</p>}
            {(step.commands || []).map((cmd, ci) => <CommandBlock key={ci} command={cmd.command} description={cmd.description} note={cmd.note} />)}
            {step.verification && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><CheckCircle className="h-5 w-5 text-green-600" /><h4 className="font-medium text-green-900 dark:text-green-300">Vérification</h4></div>
                <CodeBlock code={step.verification} language="bash" />
              </div>
            )}
            {step.troubleshooting?.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-500/10 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-5 w-5 text-yellow-600" /><h4 className="font-medium text-yellow-900 dark:text-yellow-300">Dépannage</h4></div>
                <ul className="list-disc list-inside space-y-1">{step.troubleshooting.map((t, ti) => <li key={ti} className="text-yellow-800 dark:text-yellow-200 text-sm">{t}</li>)}</ul>
              </div>
            )}
            {step.configFiles?.length > 0 && (
              <div className="mt-3 space-y-3">
                {step.configFiles.map((f, fi) => (
                  <div key={fi} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2 mb-1"><FileCode className="h-4 w-4 text-blue-500" /><span className="font-medium text-sm text-gray-800 dark:text-gray-100">{f.path}</span></div>
                    {f.description && <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{f.description}</p>}
                    {f.content && <CodeBlock code={f.content} language="text" />}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {data.postInstallation?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-green-600" />Tests post-installation</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {data.postInstallation.map((t, i) => (
              <div key={i}>
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">{t.title}</h4>
                <CommandBlock command={t.command} />
                {t.expectedOutput && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1"><strong>Résultat attendu :</strong> {t.expectedOutput}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.ports?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-blue-600" />Ports réseau</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.ports.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div><span className="font-mono font-bold text-gray-900 dark:text-white">{p.port}</span><p className="text-sm text-gray-600 dark:text-gray-300">{p.description}</p></div>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const InstallationGuide = () => {
  const components = useMemo(() => {
    return componentContext.keys().map((key) => {
      const filename = key.replace('./', '');
      try {
        const data = componentContext(key);
        if (!data.metadata || !data.platforms) return null;
        const category = detectCategory(data.metadata);
        return { filename, name: data.metadata.title || filename, description: data.metadata.description || '', version: data.metadata.version, category, data };
      } catch (e) { return null; }
    }).filter(Boolean).sort((a, b) => (a.category.localeCompare(b.category) || a.name.localeCompare(b.name)));
  }, []);

  const [selectedFilename, setSelectedFilename] = useState(components[0]?.filename || null);
  const [selectedOS, setSelectedOS] = useState('ubuntu');

  const selected = components.find((c) => c.filename === selectedFilename) || components[0] || null;
  const platforms = selected?.data.platforms || {};
  const availableOS = OPERATING_SYSTEMS.filter((o) => platforms[o.id]);
  const effectiveOS = platforms[selectedOS] ? selectedOS : (availableOS[0]?.id || null);

  if (components.length === 0) return <EmptyState icon={Package} title="Aucun composant" message="Ajoutez des fichiers dans src/data/components/." />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Installation des composants</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">{components.length} composant(s) — guides multi-OS auto-découverts.</p>
      </div>

      {/* Sélecteur de composant */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-8">
        {components.map((c) => {
          const cat = CATEGORY[c.category] || CATEGORY.Other;
          const on = selected?.filename === c.filename;
          return (
            <button key={c.filename} onClick={() => setSelectedFilename(c.filename)}
              style={on ? { borderColor: cat.color } : undefined}
              className={`p-4 rounded-lg text-left border-2 transition-all ${on ? 'bg-gray-50 dark:bg-gray-800 shadow-sm' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md'}`}>
              <div className="flex items-center gap-3 mb-1">
                <span className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${cat.color}1A`, color: cat.color }}><Icon name={cat.icon} className="h-5 w-5" /></span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate">{c.name}</h3>
                  <Badge color={cat.color}>{c.category}</Badge>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{c.description}</p>
            </button>
          );
        })}
      </div>

      {selected && (
        <>
          <Card className="mb-6">
            <CardContent>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selected.data.metadata?.title || selected.name}</h2>
              {selected.data.metadata?.description && <p className="text-gray-600 dark:text-gray-300 mt-1">{selected.data.metadata.description}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                <span>Fichier : {selected.filename}</span>
                {selected.data.metadata?.version && <><span>•</span><span>v{selected.data.metadata.version}</span></>}
                {selected.data.metadata?.sourceRef && <><span>•</span><span className="font-mono">src: {selected.data.metadata.sourceRef}</span></>}
              </div>
            </CardContent>
          </Card>

          {/* Sélecteur d'OS */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Système d'exploitation</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {OPERATING_SYSTEMS.map((os) => {
                const available = !!platforms[os.id];
                const on = effectiveOS === os.id;
                return (
                  <button key={os.id} onClick={() => available && setSelectedOS(os.id)} disabled={!available}
                    className={`p-4 rounded-lg text-left border-2 transition-all ${!available ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-400 cursor-not-allowed' : on ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md'}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{os.icon}</span>
                      <h3 className={`font-semibold ${!available ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>{os.name}</h3>
                    </div>
                    {!available && <p className="text-sm text-gray-400 mt-1">Guide non disponible</p>}
                  </button>
                );
              })}
            </div>
          </div>

          {effectiveOS ? <InstallationSteps data={selected.data} os={effectiveOS} /> : <EmptyState icon={AlertTriangle} title="Aucune plateforme documentée pour ce composant." />}
        </>
      )}
    </div>
  );
};

export default InstallationGuide;
